import {
  Divider,
  Grid,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useGetAllFunderQuery } from "api/api-funder";
import {
  useFetchIncomlendPermissionsQuery,
  useFetchUserPermissionsQuery,
  useGetCurrentUserQuery,
} from "api/api-users-v2";
import {
  ADD_PARTNER,
  DASHBOARD,
  GROUP_LIST,
  INCOMLEND_INTERNAL,
  INCOMLEND_INTERNAL_LINK,
  INCOMLEND_INTERNAL_NAME,
  ONBOARDED,
  ONBOARDING,
  PRESALES,
  REGISTERED,
  REGISTER_FOR_FINANCING,
  TP_ADDED,
} from "codes";
import SkeletonLoad from "components/Common/SkeletonLoad";
import { dispatchAccountsListViewContext } from "helpers/accountViewSync";
import { isUUID, roleRedirect } from "helpers/redirect";
import {
  ACCOUNTS_LIST_PERMISSION,
  getRoleSwitcherDisplayLabel,
  INCOMLEND_INTERNAL_VIEW_LABEL,
  isResolvableAccountApiId,
  ROLE_SWITCHER_HEADER_VALUE,
} from "helpers/viewContext";
import { useGetAccountByIdQuery } from "hooks/useGetAccountById";
import { t } from "i18next";
import { PERMISSIONS } from "permissions";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  setAppPermission,
  setAppPermissions,
  setAppState,
  setOriginalUserPermission,
  setProfile,
  setRole,
  setUserPermission,
} from "redux/reducers/appStatesReducer";
import {
  clearCurrentCompany,
  setCurrentCompany,
} from "redux/reducers/companyReducer";
import { useNavigate } from "react-router-dom";

const RolesSwitcher = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: user, isLoading: isUserLoading } = useGetCurrentUserQuery();
  const { data: userPermissions, isLoading: isLoadingUserPermissions } =
    useFetchUserPermissionsQuery(user?.id ?? "", {
      skip: !Boolean(user),
    });
  const {
    data: incomlendPermissions,
    isLoading: isLoadingIncomlendPermissions,
  } = useFetchIncomlendPermissionsQuery(user?.id ?? "", {
    skip: !Boolean(user),
  });

  const USER_ROLE = useAppSelector((state) => state.appState.role);
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission,
  );
  const ORIGINAL_USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.original_user_permission,
  );

  const shouldFetchFunders =
    USER_PERMISSIONS?.includes(PERMISSIONS.show_all_funders) ||
    ORIGINAL_USER_PERMISSIONS?.includes(PERMISSIONS.show_all_funders);

  const { data: funders, isLoading: isLoadingFunders } = useGetAllFunderQuery(
    null,
    {
      skip: !shouldFetchFunders,
    },
  );

  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);
  const APP_STATE = useAppSelector((state) => state.appState.value);
  const PRESALES_ACCOUNT_STATES = [
    REGISTERED,
    PRESALES,
    ONBOARDING,
    TP_ADDED,
    ADD_PARTNER,
  ];
  const initialRole = userPermissions?.[0]?.id ?? "";
  const [id, setId] = useState<string>(USER_ROLE?.id ?? initialRole);

  useEffect(() => {
    if (APP_PERMISSION === INCOMLEND_INTERNAL) {
      setId(INCOMLEND_INTERNAL);
    } else if (APP_PERMISSION === ACCOUNTS_LIST_PERMISSION) {
      setId(ACCOUNTS_LIST_PERMISSION);
    } else if (USER_ROLE?.id) {
      setId(USER_ROLE.id);
    }
  }, [APP_PERMISSION, USER_ROLE?.id]);

  const AVAILABLE_ROLE_NAMES: any[] = [];
  if (incomlendPermissions && Boolean(incomlendPermissions?.groups?.length))
    AVAILABLE_ROLE_NAMES.push({
      id: INCOMLEND_INTERNAL,
      name: INCOMLEND_INTERNAL_NAME,
      permission: incomlendPermissions.permissions,
      role: INCOMLEND_INTERNAL,
    });
  if (userPermissions) {
    if (userPermissions?.length > 1)
      AVAILABLE_ROLE_NAMES.push({
        id: ACCOUNTS_LIST_PERMISSION,
        name: "Accounts",
        permission: [],
        role: ACCOUNTS_LIST_PERMISSION,
      });
    else if (userPermissions?.length === 1)
      AVAILABLE_ROLE_NAMES.push(userPermissions?.[0]);
  }

  const hasFundersPermission =
    USER_PERMISSIONS?.includes(PERMISSIONS.show_all_funders) ||
    ORIGINAL_USER_PERMISSIONS?.includes(PERMISSIONS.show_all_funders);

  if (hasFundersPermission && funders && funders.length > 0) {
    AVAILABLE_ROLE_NAMES.push({
      id: "Funders",
      name: "Funders",
      permission: [],
      role: "Funders",
    });
  }

  const hasReportsPermission =
    USER_PERMISSIONS?.includes(PERMISSIONS.show_all_reports) ||
    ORIGINAL_USER_PERMISSIONS?.includes(PERMISSIONS.show_all_reports) ||
    USER_PERMISSIONS?.includes(PERMISSIONS.show_my_reports) ||
    ORIGINAL_USER_PERMISSIONS?.includes(PERMISSIONS.show_my_reports);

  if (hasReportsPermission) {
    AVAILABLE_ROLE_NAMES.push({
      id: "Reports",
      name: "Reports",
      permission: [],
      role: "Reports",
      redirectUrl: "https://reports.incomlend.com",
    });
  }

  const { data: company } = useGetAccountByIdQuery(id, {
    skip:
      GROUP_LIST.includes(APP_PERMISSION) ||
      APP_PERMISSION === "Funders" ||
      (APP_PERMISSION === "Investor" &&
        funders?.some((funder) => funder.id === id)),
  });

  useEffect(() => {
    if (!isResolvableAccountApiId(id)) {
      dispatch(clearCurrentCompany());
      return;
    }
    if (company) {
      dispatch(setCurrentCompany(company));
      if (company.profile) {
        dispatch(setProfile(company.profile));
      }
    }
  }, [company, dispatch, id]);

  const leaveAccountContext = () => {
    dispatch(clearCurrentCompany());
  };

  const applyInternalView = () => {
    const internalPermissions =
      incomlendPermissions?.permissions ??
      ORIGINAL_USER_PERMISSIONS ??
      USER_PERMISSIONS;

    leaveAccountContext();
    dispatch(setAppState(""));
    dispatch(setAppPermission(INCOMLEND_INTERNAL));
    dispatch(
      setRole({
        id: INCOMLEND_INTERNAL,
        name: INCOMLEND_INTERNAL_NAME,
        permission: internalPermissions,
        role: INCOMLEND_INTERNAL,
      }),
    );
    if (incomlendPermissions?.groups?.length) {
      dispatch(setAppPermissions(incomlendPermissions.groups));
    }
    if (internalPermissions?.length) {
      dispatch(setUserPermission(internalPermissions));
    }
    setId(INCOMLEND_INTERNAL);
    navigate(INCOMLEND_INTERNAL_LINK, { replace: true });
  };

  const handleChange = (event: SelectChangeEvent) => {
    const retreivedId = event.target.value;

    if (retreivedId === ROLE_SWITCHER_HEADER_VALUE) {
      return;
    }

    if (retreivedId === INCOMLEND_INTERNAL) {
      applyInternalView();
      return;
    }

    if (retreivedId === ACCOUNTS_LIST_PERMISSION) {
      leaveAccountContext();
      dispatchAccountsListViewContext(dispatch);
      setId(ACCOUNTS_LIST_PERMISSION);
      navigate("/accounts", { replace: true });
      return;
    }

    if (retreivedId === "Funders") {
      const permissionsToRestore =
        ORIGINAL_USER_PERMISSIONS && ORIGINAL_USER_PERMISSIONS.length > 0
          ? ORIGINAL_USER_PERMISSIONS
          : USER_PERMISSIONS;

      leaveAccountContext();
      dispatch(setAppPermission("Funders"));
      dispatch(
        setRole({
          id: "Funders",
          name: "Funders",
          permission: [],
          role: "Funders",
        }),
      );
      if (permissionsToRestore?.length) {
        dispatch(setUserPermission(permissionsToRestore));
      }
      setId("Funders");
      navigate("/funders-selection", {
        replace: true,
        state: { from: window.location.pathname + window.location.search },
      });
      return;
    }

    if (retreivedId === "Reports") {
      leaveAccountContext();
      dispatch(setAppPermission("Reports"));
      dispatch(
        setRole({
          id: "Reports",
          name: "Reports",
          permission: [],
          role: "Reports",
        }),
      );
      setId("Reports");
      navigate("/reports-selections", {
        replace: true,
        state: { from: window.location.pathname + window.location.search },
      });
      return;
    }

    const isSelectingClientAccount = isUUID.test(retreivedId);

    setId(retreivedId);
    const user_permission =
      userPermissions?.find((role) => role.id === retreivedId)?.permission ??
      [];

    const user_role = userPermissions?.find((role) => role.id === retreivedId);

    if (
      isSelectingClientAccount &&
      !ORIGINAL_USER_PERMISSIONS?.length &&
      incomlendPermissions?.permissions?.length
    ) {
      dispatch(
        setOriginalUserPermission(incomlendPermissions.permissions ?? []),
      );
    }

    dispatch(setAppPermission(retreivedId));
    dispatch(setUserPermission(user_permission));

    if (
      user_permission.includes(PERMISSIONS.show_risk_management) &&
      incomlendPermissions
    ) {
      dispatch(
        setUserPermission([
          ...user_permission,
          ...incomlendPermissions?.permissions,
        ]),
      );
      dispatch(setAppPermission(INCOMLEND_INTERNAL));
    }

    if (user_role) {
      dispatch(
        setRole({
          ...user_role,
          role: user_role.director ? "Director" : "Non-Director",
        }),
      );
      if (user_role.account_state) {
        dispatch(setAppState(user_role.account_state));
      }
      if (user_role.account_profile) {
        dispatch(setProfile(user_role.account_profile));
      }
      dispatch(setAppPermissions(user_role.permission ?? []));
    }

    if (isSelectingClientAccount && user_role?.account_state) {
      if (PRESALES_ACCOUNT_STATES.includes(user_role.account_state)) {
        navigate(REGISTER_FOR_FINANCING, { replace: true });
        return;
      }
      if (user_role.account_state === ONBOARDED) {
        navigate(DASHBOARD, { replace: true });
        return;
      }
    }

    roleRedirect(isSelectingClientAccount ? "Client" : retreivedId);
  };

  const selectValue = id !== "" ? id : (USER_ROLE?.id ?? "");

  const switcherDisplayLabel = getRoleSwitcherDisplayLabel(selectValue, {
    appPermission: APP_PERMISSION,
    appState: APP_STATE,
    roleName: USER_ROLE?.name,
    companyName: company?.name,
    menuOptions: AVAILABLE_ROLE_NAMES,
  });

  const IS_MULTI_ACCOUNT =
    userPermissions &&
    AVAILABLE_ROLE_NAMES?.length &&
    userPermissions?.length > 1;

  const IS_MULTI_ROLE =
    userPermissions &&
    AVAILABLE_ROLE_NAMES?.length &&
    AVAILABLE_ROLE_NAMES.length > 1;

  return (
    <Grid item>
      {(isUserLoading ||
        isLoadingUserPermissions ||
        isLoadingIncomlendPermissions ||
        isLoadingFunders) && <SkeletonLoad bars={1} />}
      {!isUserLoading && (IS_MULTI_ACCOUNT || IS_MULTI_ROLE) ? (
        <Select
          value={selectValue}
          renderValue={() => switcherDisplayLabel}
          onChange={handleChange}
          label="Role"
          id="roles-switcher"
          displayEmpty
          fullWidth
          sx={{
            padding: "1ch",
            border: "1px solid",
            textTransform: "capitalize",
            overflow: "hidden",
            maxWidth: "100%",
            textOverflow: "ellipsis",
            ".MuiSelect-select": {
              padding: "0 0 0 10px",
              fontWeight: 600,
            },
            ">div": {
              borderRadius: "10px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              maxWidth: "76%",
              textOverflow: "ellipsis",
            },
          }}
        >
          <MenuItem value={ROLE_SWITCHER_HEADER_VALUE} disabled>
            {switcherDisplayLabel}
          </MenuItem>
          <Divider />
          {AVAILABLE_ROLE_NAMES?.map((role) => {
            const roleLabel =
              role?.id === INCOMLEND_INTERNAL
                ? INCOMLEND_INTERNAL_VIEW_LABEL
                : role?.name;
            return (
              <MenuItem value={role?.id} key={role?.id}>
                <Typography fontWeight={600} color="primary">
                  {roleLabel}
                </Typography>
              </MenuItem>
            );
          })}
        </Select>
      ) : (
        <Typography
          fontWeight={800}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {AVAILABLE_ROLE_NAMES[0]?.name ?? t("welcome")}
        </Typography>
      )}
    </Grid>
  );
};

export default RolesSwitcher;
